import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../reducers';
import { resetStore } from '../actions/storeReset';
import { selectIsOptimizing } from './territoryManagementSlice';

/**
 * Boundary type options for the optimization wizard
 */
export type BoundaryType =
  | 'region'
  | 'territory'
  | 'state'
  | 'county'
  | 'zipcode'
  | 'draw'
  | 'radius';

/**
 * Boundary types that use the map drawing/selection infrastructure
 */
export const ADHOC_BOUNDARY_MODES: BoundaryType[] = [
  'state',
  'county',
  'zipcode',
  'draw',
  'radius',
];

/**
 * Summary statistic for a single metric across optimized territories
 */
export interface MetricSummaryStatistic {
  min: number;
  max: number;
  mean: number;
  std_dev: number;
}

/**
 * A single territory within an optimization result
 */
export interface OptimizationTerritory {
  index: number;
  boundary_ids: string[];
  metrics: Record<string, number>;
}

/**
 * Rep assignment for a seeded territory, derived from the rep-seed dataset.
 * `label` is the rep name taken from the uploaded seed CSV; `user_id` is the
 * linked MedScout user when the seed row was matched, otherwise null.
 */
export interface RepAssignment {
  label: string;
  user_id: number | null;
}

/**
 * Full payload returned from the optimization API result field
 */
export interface OptimizationResultPayload {
  balance_score: number;
  territories: OptimizationTerritory[];
  summary_statistics: Record<string, MetricSummaryStatistic>;
}

/**
 * A single display option within a metric display value group
 */
export interface MetricDisplayOption {
  code: string;
  type: string;
  label: string;
}

/**
 * A grouped metric display value from the API (e.g., "X-Ray", "MRI").
 *
 * Two entry shapes appear in `metric_display_values` — the presence of the id
 * key is the discriminator (never both): strategy entries carry
 * `strategy_metric_id`; custom (customer-dataset) entries carry
 * `customer_dataset_metric_id`.
 */
export interface MetricDisplayValue {
  strategy_metric_id?: number;
  customer_dataset_metric_id?: number;
  label: string;
  display_options: MetricDisplayOption[];
}

/**
 * Where a refinement version originated. Mirrors the backend
 * `TerritoryOptimizationOperationSource` text choices (lowercase).
 */
export type OperationSource = 'chat' | 'map';

/**
 * A non-blocking warning attached to a refinement version. Today the only code
 * is `non_contiguous` (a territory split into disconnected parts); typed loosely
 * so future warning codes flow through without a contract change.
 */
export interface RefinementWarning {
  code: string;
  territory_index?: number;
  detail?: string;
}

/**
 * Metadata describing the operation that produced a version. `null` for the v0
 * baseline (the original job result). `index_remapping` is `{old: new}` for
 * operations that renumber territories (merge/split); empty for a move.
 */
export interface RefinementOperationMeta {
  type: string;
  params?: Record<string, unknown>;
  summary?: string;
  index_remapping?: Record<string, number>;
}

/**
 * Version-strip list item — one refinement revision of an optimization job.
 * v0 (operation/source null) is the original result; higher numbers are edits.
 */
export interface OptimizationVersionSummary {
  version_number: number;
  operation: RefinementOperationMeta | null;
  operation_source: OperationSource | null;
  balance_score: number | null;
  warnings: RefinementWarning[];
  created_at: string | null;
  is_active: boolean;
}

/**
 * A version summary plus the full result snapshot needed to render the map —
 * returned by the version-detail endpoint and pushed on the `result_version`
 * WebSocket event. `rep_assignments` is version-scoped (split/merge renumber
 * territories, so the job-level map can point at the wrong ones).
 */
export interface OptimizationVersionDetail extends OptimizationVersionSummary {
  result: OptimizationResultPayload | null;
  rep_assignments: Record<string, RepAssignment> | null;
  metric_display_values: MetricDisplayValue[];
}

/**
 * Live balance preview for a pending (uncommitted) Reassign-mode move. Produced
 * by `useRefinementPreview` from a debounced `previewRefinement` call and read by
 * `ResultsMetricsStrip`. `null` whenever there is nothing staged to preview.
 */
export interface RefinementPreviewState {
  /** Destination territory index of the previewed move. */
  targetIndex: number;
  /** Number of units in the previewed move (after no-op skips). */
  unitCount: number;
  oldBalanceScore: number;
  newBalanceScore: number;
  balanceScoreDelta: number;
  /** Per-territory metric deltas (previewed minus current) for changed territories. */
  territoryMetricDeltas: Array<{
    index: number;
    deltas: Record<string, number>;
  }>;
  /** Warnings the move would introduce (e.g. `non_contiguous`), shown pre-commit. */
  warnings: RefinementWarning[];
}

/**
 * State for Territory Optimization Wizard
 * Manages the multi-step optimization flow
 */
export interface OptimizationState {
  /** Current step in the wizard (0-indexed, 5 steps total) */
  currentStep: 0 | 1 | 2 | 3 | 4;
  /** Selected boundary type for Step 0 */
  boundaryType: BoundaryType;
  /** IDs of territories selected for optimization */
  selectedTerritoryIds: number[];
  /** IDs of regions selected for optimization */
  selectedRegionIds: number[];
  /** ID of the selected strategy */
  selectedStrategyId: number | null;
  /** Optimization parameters */
  parameters: {
    focus: ('claims' | 'patients' | 'providers')[];
    numberOfTerritories: number;
    snapBoundariesTo: 'states' | 'counties' | 'zipcodes';
  };
  /** Whether optimization is currently processing */
  isProcessing: boolean;
  /** Results from the optimization API */
  results: OptimizationResultPayload | null;
  /** Adhoc boundary IDs captured from context (state/county/zip clicked features) */
  adhocBoundaryIds: string[];
  /** Adhoc drawn polygon coordinates */
  adhocDrawnPolygon: Array<{ lat: number; lng: number }[]> | null;
  /** Adhoc radius circles */
  adhocCircles: Array<{
    center: { lat: number; lng: number };
    radius: number;
  }> | null;
  /** Job ID from the optimization API */
  jobId: number | null;
  /** Error message from the optimization */
  error: string | null;
  /**
   * Whether the optimization is provider-focused (true), site-focused (false),
   * or not yet / can't be determined (null → entity count labeled generically).
   */
  isProviderFocused: boolean | null;
  /** Metric display values from the API for grouping metrics in results */
  metricDisplayValues: MetricDisplayValue[];
  /** Which result territories are selected (keyed by territory index) */
  selectedResultTerritories: Record<number, boolean>;
  /** Assigned rep user ID per result territory (keyed by territory index) */
  assignedReps: Record<number, string>;
  /** Seed-derived rep assignment per result territory (keyed by territory index) */
  repAssignments: Record<number, RepAssignment>;
  /** Territory index currently hovered in the results panel (null if none) */
  hoveredResultTerritory: number | null;
  /** Custom territory names (keyed by territory index) */
  territoryNames: Record<number, string>;
  /**
   * Refinement version the results map currently renders. `null` until versions
   * are loaded; `0` is the original (v0) result. The map always shows the active
   * version — older versions are inspected via the compare view.
   */
  activeVersionNumber: number | null;
  /** Refinement version history for the active job (v0 first, then edits asc). */
  versions: OptimizationVersionSummary[];
  /** Live balance preview for a pending Reassign move (null when nothing staged). */
  refinementPreview: RefinementPreviewState | null;
  /**
   * User-facing message when a pending move can't be previewed/applied (e.g. it
   * targets a rep-seed anchor unit). Optional so existing state literals need no
   * change; absent/null means no error.
   */
  refinementPreviewError?: string | null;
  /**
   * Non-contiguous territory indices the user has acknowledged, keyed by version
   * number. A dismissal follows the territory into later versions while it stays
   * split, and is dropped once it isn't. Frontend-only; cleared on job switch.
   * See MKT-836. Optional so existing state literals need no change.
   */
  dismissedContiguityWarnings?: Record<number, number[]>;
}

const initialState: OptimizationState = {
  currentStep: 0,
  boundaryType: 'region',
  selectedTerritoryIds: [],
  selectedRegionIds: [],
  selectedStrategyId: null,
  parameters: {
    focus: [],
    numberOfTerritories: 3,
    snapBoundariesTo: 'counties',
  },
  isProcessing: false,
  results: null,
  adhocBoundaryIds: [],
  adhocDrawnPolygon: null,
  adhocCircles: null,
  jobId: null,
  error: null,
  // Unknown until hydrated from the job's strategy — the entity count reads as a
  // generic "Entities" rather than defaulting to Sites/Providers.
  isProviderFocused: null,
  metricDisplayValues: [],
  selectedResultTerritories: {},
  assignedReps: {},
  repAssignments: {},
  hoveredResultTerritory: null,
  territoryNames: {},
  activeVersionNumber: null,
  versions: [],
  refinementPreview: null,
  refinementPreviewError: null,
  dismissedContiguityWarnings: {},
};

/** Territory indices a set of version warnings flags as non-contiguous. */
const getNonContiguousIndices = (warnings: RefinementWarning[] | undefined) =>
  (warnings ?? [])
    .filter(
      (w) => w.code === 'non_contiguous' && Number.isFinite(w.territory_index)
    )
    .map((w) => w.territory_index);

export const optimizationSlice = createSlice({
  name: 'optimization',
  initialState,
  reducers: {
    /**
     * Set the current wizard step
     */
    setStep: (state, action: PayloadAction<0 | 1 | 2 | 3 | 4>) => {
      state.currentStep = action.payload;
    },
    /**
     * Go to the next step (if not at the last step)
     */
    nextStep: (state) => {
      if (state.currentStep < 4) {
        state.currentStep = (state.currentStep + 1) as 0 | 1 | 2 | 3 | 4;
      }
    },
    /**
     * Go to the previous step (if not at the first step)
     */
    prevStep: (state) => {
      if (state.currentStep > 0) {
        state.currentStep = (state.currentStep - 1) as 0 | 1 | 2 | 3 | 4;
      }
    },
    /**
     * Set the boundary type and clear all selections
     */
    setBoundaryType: (state, action: PayloadAction<BoundaryType>) => {
      state.boundaryType = action.payload;
      state.selectedTerritoryIds = [];
      state.selectedRegionIds = [];
      state.adhocBoundaryIds = [];
      state.adhocDrawnPolygon = null;
      state.adhocCircles = null;
    },
    /**
     * Set the selected territory IDs for optimization
     */
    setSelectedTerritories: (state, action: PayloadAction<number[]>) => {
      state.selectedTerritoryIds = action.payload;
    },
    /**
     * Toggle a territory's selection status
     */
    toggleTerritorySelection: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      const index = state.selectedTerritoryIds.indexOf(id);
      if (index === -1) {
        state.selectedTerritoryIds.push(id);
      } else {
        state.selectedTerritoryIds.splice(index, 1);
      }
    },
    /**
     * Set the selected region IDs for optimization
     */
    setSelectedRegions: (state, action: PayloadAction<number[]>) => {
      state.selectedRegionIds = action.payload;
    },
    /**
     * Toggle a region's selection status
     */
    toggleRegionSelection: (state, action: PayloadAction<number>) => {
      const id = action.payload;
      const index = state.selectedRegionIds.indexOf(id);
      if (index === -1) {
        state.selectedRegionIds.push(id);
      } else {
        state.selectedRegionIds.splice(index, 1);
      }
    },
    /**
     * Set the selected strategy
     */
    setStrategy: (state, action: PayloadAction<number | null>) => {
      state.selectedStrategyId = action.payload;
    },
    /**
     * Set the optimization parameters
     */
    setParameters: (
      state,
      action: PayloadAction<{
        focus?: ('claims' | 'patients' | 'providers')[];
        numberOfTerritories?: number;
        snapBoundariesTo?: 'states' | 'counties' | 'zipcodes';
      }>
    ) => {
      state.parameters = { ...state.parameters, ...action.payload };
    },
    /**
     * Set the processing state
     */
    setIsProcessing: (state, action: PayloadAction<boolean>) => {
      state.isProcessing = action.payload;
    },
    /**
     * Set the optimization results
     */
    setResults: (
      state,
      action: PayloadAction<OptimizationResultPayload | null>
    ) => {
      state.results = action.payload;
    },
    /**
     * Capture adhoc boundary data from context state
     */
    captureAdhocBoundaries: (
      state,
      action: PayloadAction<{
        boundaryIds?: string[];
        drawnPolygon?: Array<{ lat: number; lng: number }[]> | null;
        circles?: Array<{
          center: { lat: number; lng: number };
          radius: number;
        }> | null;
      }>
    ) => {
      if (action.payload.boundaryIds !== undefined) {
        state.adhocBoundaryIds = action.payload.boundaryIds;
      }
      if (action.payload.drawnPolygon !== undefined) {
        state.adhocDrawnPolygon = action.payload.drawnPolygon;
      }
      if (action.payload.circles !== undefined) {
        state.adhocCircles = action.payload.circles;
      }
    },
    /**
     * Set the job ID from the optimization API
     */
    setJobId: (state, action: PayloadAction<number | null>) => {
      state.jobId = action.payload;
    },
    /**
     * Set the error message
     */
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    /**
     * Set whether the optimization is provider-focused. `null` means unknown /
     * not yet determined — the entity count then reads as generic "Entities".
     */
    setIsProviderFocused: (state, action: PayloadAction<boolean | null>) => {
      state.isProviderFocused = action.payload;
    },
    /**
     * Set metric display values from the API response
     */
    setMetricDisplayValues: (
      state,
      action: PayloadAction<MetricDisplayValue[]>
    ) => {
      state.metricDisplayValues = action.payload;
    },
    /**
     * Initialize result territory selection (all selected by default)
     */
    initResultTerritorySelection: (
      state,
      action: PayloadAction<number[]>
    ) => {
      const selection: Record<number, boolean> = {};
      action.payload.forEach((idx) => {
        selection[idx] = true;
      });
      state.selectedResultTerritories = selection;
    },
    /**
     * Toggle a single result territory's selection
     */
    toggleResultTerritorySelection: (
      state,
      action: PayloadAction<number>
    ) => {
      const idx = action.payload;
      state.selectedResultTerritories[idx] =
        !state.selectedResultTerritories[idx];
    },
    /**
     * Select all result territories
     */
    selectAllResultTerritories: (state) => {
      Object.keys(state.selectedResultTerritories).forEach((key) => {
        state.selectedResultTerritories[Number(key)] = true;
      });
    },
    /**
     * Deselect all result territories
     */
    deselectAllResultTerritories: (state) => {
      Object.keys(state.selectedResultTerritories).forEach((key) => {
        state.selectedResultTerritories[Number(key)] = false;
      });
    },
    /**
     * Set the seed-derived rep assignments from the optimization result.
     * Incoming keys are stringified territory indices (JSON object keys);
     * they are normalized to numeric keys to match `OptimizationTerritory.index`.
     */
    setRepAssignments: (
      state,
      action: PayloadAction<
        Record<string, RepAssignment> | null | undefined
      >
    ) => {
      const normalized: Record<number, RepAssignment> = {};
      Object.entries(action.payload ?? {}).forEach(([key, value]) => {
        normalized[Number(key)] = value;
      });
      state.repAssignments = normalized;
    },
    /**
     * Set the assigned rep for a result territory
     */
    setAssignedRep: (
      state,
      action: PayloadAction<{ territoryIndex: number; userId: string }>
    ) => {
      state.assignedReps[action.payload.territoryIndex] =
        action.payload.userId;
    },
    /**
     * Set a custom name for a result territory
     */
    setTerritoryName: (
      state,
      action: PayloadAction<{ territoryIndex: number; name: string }>
    ) => {
      state.territoryNames[action.payload.territoryIndex] =
        action.payload.name;
    },
    /**
     * Set the hovered result territory index (null to clear)
     */
    setHoveredResultTerritory: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.hoveredResultTerritory = action.payload;
    },
    /**
     * Set the full refinement version history (e.g. from the versions-list
     * endpoint on load). Resolves the active version from the `is_active` flag,
     * falling back to the highest-numbered version, or null when empty.
     */
    setVersions: (
      state,
      action: PayloadAction<OptimizationVersionSummary[]>
    ) => {
      state.versions = action.payload;
      // An empty list is the job-switch reset; drop the previous job's dismissals.
      if (!action.payload.length) state.dismissedContiguityWarnings = {};
      const active = action.payload.find((v) => v.is_active);
      state.activeVersionNumber = active
        ? active.version_number
        : action.payload.length
          ? action.payload[action.payload.length - 1].version_number
          : null;
    },
    /**
     * Make a version's snapshot the active one the map renders. Writes into the
     * same `results` / `repAssignments` / `metricDisplayValues` fields the
     * results map already reads, so the map updates with no extra wiring.
     * Rep-assignment keys are normalized to numbers (see `setRepAssignments`).
     */
    applyVersion: (
      state,
      action: PayloadAction<OptimizationVersionDetail>
    ) => {
      const version = action.payload;
      // A version with no dismissals of its own inherits the latest earlier
      // version's: renumbered through the operation's `{old: new}` remapping (an
      // index it doesn't list keeps its number), keeping only territories still
      // flagged. Keyed off version numbers rather than the previously active one,
      // so it holds even if a version-list refetch moved the active number first.
      const dismissals = state.dismissedContiguityWarnings ?? {};
      const previousNumber = Math.max(
        -1,
        ...Object.keys(dismissals)
          .map(Number)
          .filter((n) => n < version.version_number)
      );
      const carried = dismissals[previousNumber];
      if (carried?.length && !dismissals[version.version_number]) {
        const remapping = version.operation?.index_remapping ?? {};
        const stillFlagged = new Set(getNonContiguousIndices(version.warnings));
        state.dismissedContiguityWarnings = {
          ...dismissals,
          [version.version_number]: carried
            .map((index) => remapping[String(index)] ?? index)
            .filter((index) => stillFlagged.has(index)),
        };
      }
      state.results = version.result;
      state.metricDisplayValues = version.metric_display_values ?? [];
      const normalized: Record<number, RepAssignment> = {};
      Object.entries(version.rep_assignments ?? {}).forEach(([key, value]) => {
        normalized[Number(key)] = value;
      });
      state.repAssignments = normalized;
      state.activeVersionNumber = version.version_number;
      // A newly authoritative version invalidates any pending pre-commit preview.
      state.refinementPreview = null;
      state.refinementPreviewError = null;
      // Keep the strip's active flag consistent with the now-active version.
      state.versions = state.versions.map((summary) => ({
        ...summary,
        is_active: summary.version_number === version.version_number,
      }));
    },
    /**
     * Insert or update a single version summary in the strip (e.g. a newly
     * created version arriving over the WebSocket). Kept sorted by number.
     */
    upsertVersionSummary: (
      state,
      action: PayloadAction<OptimizationVersionSummary>
    ) => {
      const incoming = action.payload;
      const idx = state.versions.findIndex(
        (v) => v.version_number === incoming.version_number
      );
      if (idx >= 0) {
        state.versions[idx] = incoming;
      } else {
        state.versions.push(incoming);
        state.versions.sort((a, b) => a.version_number - b.version_number);
      }
    },
    /**
     * After an undo/delete, drop every version numbered above the now-active
     * one and mark the active version. The active result falls back to the
     * highest remaining version (matching the backend `active_result`).
     */
    pruneVersionsAbove: (state, action: PayloadAction<number>) => {
      const active = action.payload;
      state.versions = state.versions
        .filter((v) => v.version_number <= active)
        .map((summary) => ({
          ...summary,
          is_active: summary.version_number === active,
        }));
      state.activeVersionNumber = active;
      // The remaining version's own dismissals apply again; the pruned ones go.
      state.dismissedContiguityWarnings = Object.fromEntries(
        Object.entries(state.dismissedContiguityWarnings ?? {}).filter(
          ([versionNumber]) => Number(versionNumber) <= active
        )
      );
    },
    /**
     * Acknowledge a territory's non-contiguous warning on the active version,
     * hiding its map outline and legend icon. See MKT-836.
     */
    dismissContiguityWarning: (state, action: PayloadAction<number>) => {
      const versionNumber = state.activeVersionNumber;
      if (versionNumber == null) return;
      const dismissals = state.dismissedContiguityWarnings ?? {};
      const current = dismissals[versionNumber] ?? [];
      if (current.includes(action.payload)) return;
      state.dismissedContiguityWarnings = {
        ...dismissals,
        [versionNumber]: [...current, action.payload],
      };
    },
    /**
     * Store the live balance preview for a pending Reassign move. Set by
     * `useRefinementPreview` after a debounced `previewRefinement` resolves.
     */
    setRefinementPreview: (
      state,
      action: PayloadAction<RefinementPreviewState>
    ) => {
      state.refinementPreview = action.payload;
      state.refinementPreviewError = null;
    },
    /**
     * Record that the pending move can't be previewed/applied (e.g. it targets a
     * rep-seed anchor unit). Clears any stale preview so only the error shows.
     */
    setRefinementPreviewError: (state, action: PayloadAction<string>) => {
      state.refinementPreview = null;
      state.refinementPreviewError = action.payload;
    },
    /** Clear the pending-move preview (selection emptied, exited, or committed). */
    clearRefinementPreview: (state) => {
      state.refinementPreview = null;
      state.refinementPreviewError = null;
    },
    /**
     * Reset all optimization state to initial values
     */
    resetOptimization: () => initialState,
  },
  extraReducers: (builder) => {
    // Handle global store reset for user impersonation
    builder.addCase(resetStore, () => initialState);
  },
});

// Export actions
export const {
  setStep,
  nextStep,
  prevStep,
  setBoundaryType,
  setSelectedTerritories,
  toggleTerritorySelection,
  setSelectedRegions,
  toggleRegionSelection,
  setStrategy,
  setParameters,
  setIsProcessing,
  setResults,
  captureAdhocBoundaries,
  setJobId,
  setError,
  setIsProviderFocused,
  setMetricDisplayValues,
  initResultTerritorySelection,
  toggleResultTerritorySelection,
  selectAllResultTerritories,
  deselectAllResultTerritories,
  setRepAssignments,
  setAssignedRep,
  setTerritoryName,
  setHoveredResultTerritory,
  setVersions,
  applyVersion,
  upsertVersionSummary,
  pruneVersionsAbove,
  dismissContiguityWarning,
  setRefinementPreview,
  setRefinementPreviewError,
  clearRefinementPreview,
  resetOptimization,
} = optimizationSlice.actions;

// Selectors
export const selectCurrentStep = (state: RootState) =>
  state.optimization.currentStep;

export const selectBoundaryType = (state: RootState) =>
  state.optimization.boundaryType;

export const selectSelectedTerritoryIds = (state: RootState) =>
  state.optimization.selectedTerritoryIds;

export const selectSelectedRegionIds = (state: RootState) =>
  state.optimization.selectedRegionIds;

export const selectSelectedStrategyId = (state: RootState) =>
  state.optimization.selectedStrategyId;

export const selectParameters = (state: RootState) =>
  state.optimization.parameters;

export const selectIsProcessing = (state: RootState) =>
  state.optimization.isProcessing;

export const selectResults = (state: RootState) => state.optimization.results;

export const selectAdhocBoundaryIds = (state: RootState) =>
  state.optimization.adhocBoundaryIds;

export const selectAdhocDrawnPolygon = (state: RootState) =>
  state.optimization.adhocDrawnPolygon;

export const selectAdhocCircles = (state: RootState) =>
  state.optimization.adhocCircles;

/**
 * True when optimizing with a map-interactive boundary type (state/county/zipcode/draw/radius).
 * Used to widen gate conditions in map/drawing infrastructure.
 */
export const selectIsOptimizingBoundaryMode = createSelector(
  [selectIsOptimizing, selectBoundaryType],
  (isOptimizing, boundaryType) =>
    isOptimizing && ADHOC_BOUNDARY_MODES.includes(boundaryType)
);

export const selectJobId = (state: RootState) => state.optimization.jobId;

export const selectError = (state: RootState) => state.optimization.error;

export const selectIsProviderFocused = (state: RootState) =>
  state.optimization.isProviderFocused;

export const selectMetricDisplayValues = (state: RootState) =>
  state.optimization.metricDisplayValues;

export const selectSelectedResultTerritories = (state: RootState) =>
  state.optimization.selectedResultTerritories;

export const selectAssignedReps = (state: RootState) =>
  state.optimization.assignedReps;

export const selectRepAssignments = (state: RootState) =>
  state.optimization.repAssignments;

export const selectHoveredResultTerritory = (state: RootState) =>
  state.optimization.hoveredResultTerritory;

export const selectTerritoryNames = (state: RootState) =>
  state.optimization.territoryNames;

export const selectActiveVersionNumber = (state: RootState) =>
  state.optimization.activeVersionNumber;

export const selectVersions = (state: RootState) =>
  state.optimization.versions;

export const selectRefinementPreview = (state: RootState) =>
  state.optimization.refinementPreview;

export const selectRefinementPreviewError = (state: RootState) =>
  state.optimization.refinementPreviewError ?? null;

/**
 * Warnings carried by the currently-active version (drives the map's contiguity
 * indication). Empty when there is no active version or it has none. Memoized so
 * the array identity is stable across unrelated state changes.
 */
export const selectActiveVersionWarnings = createSelector(
  [selectVersions, selectActiveVersionNumber],
  (versions, activeNumber): RefinementWarning[] =>
    versions.find((v) => v.version_number === activeNumber)?.warnings ?? []
);

const selectDismissedContiguityWarnings = (state: RootState) =>
  state.optimization.dismissedContiguityWarnings;

/**
 * Territory indices the active version flags as non-contiguous, minus those the
 * user has dismissed. Drives the map's amber outline and the legend's warning
 * icon. Memoized for a stable array identity.
 */
export const selectNonContiguousTerritoryIndices = createSelector(
  [
    selectActiveVersionWarnings,
    selectDismissedContiguityWarnings,
    selectActiveVersionNumber,
  ],
  (warnings, dismissals, activeNumber): number[] => {
    const dismissed = new Set(
      activeNumber != null ? (dismissals?.[activeNumber] ?? []) : []
    );
    return getNonContiguousIndices(warnings).filter(
      (index) => !dismissed.has(index)
    );
  }
);

// Export reducer
export default optimizationSlice.reducer;
